import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TicketView } from '../../../shared/models/ticket.model';
import { TicketUiModel } from '../../../shared/ui-models/ticket.ui-model';
import {
  cachedHelpdeskMe,
  getHelpdeskMe,
  getTicketOverviews,
  getTickets
} from '../../../shared/services/data/ticket.data';
import { HelpdeskMeModel, TicketOverviewModel } from '../../../shared/models/ticket.model';
import { hasRole } from '../../../shared/services/helpers/auth-helper/auth-helper.service';
import { UserRole } from '../../../shared/enums/user-role.enum';
import { QueueFiltersBar } from './queue-filters.component';
import { RoutingModal } from './routing-modal.component';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { LoadingData } from '../../../ui/LoadingData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import { RaiseTicketModal } from '../../../shared/components/group-ui/raise-ticket/raise-ticket-modal.component';
import {
  CHIP,
  PRIORITY_STYLE,
  SLA_LABEL,
  SLA_STYLE,
  dueIn,
  stateStyle,
  titleCase
} from '../../shared/ticket-visuals';
import {
  EMPTY_FILTERS,
  NEW_HIGHLIGHT_MS,
  QUEUE_REFRESH_MS,
  QueueFilters,
  QueueSort,
  filterOptions,
  filterQueue,
  loadQueueState,
  newArrivals,
  saveQueueState,
  sortQueue
} from '../../shared/ticket-queue.util';

const CONTENT_CLASS = 'max-w-[1440px] mx-auto px-[clamp(16px,3vw,48px)]';
const TH =
  'px-[16px] py-[12px] text-left text-[12px] font-gilroy-medium text-neutral-300 whitespace-nowrap';
const TD = 'px-[16px] py-[14px] text-[14px] text-neutral-500 whitespace-nowrap align-middle';
const HEADER_CHIP =
  'inline-flex items-center gap-[4px] px-[12px] py-[4px] rounded-full text-[13px] font-gilroy-medium whitespace-nowrap';
const OUTLINE_BUTTON =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] border border-gray-200 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:border-neutral-500 transition-colors cursor-pointer whitespace-nowrap';
const PRIMARY_BUTTON =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-primary-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap';

/**
 * Four queues, matching how people actually come looking: what I hold, what nobody
 * holds yet, everything open in my teams, and what I raised elsewhere. The first
 * three read Zammad overviews, so a new ticket shows up at once. "Raised by me"
 * reads search, which can lag a few seconds behind.
 */
const VIEWS: { key: TicketView; label: string }[] = [
  { key: 'mine', label: 'Mine' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'team', label: 'My teams' },
  { key: 'raised', label: 'Raised by me' },
  // Shown to people with reports (and helpdesk admins): their reports' open tickets.
  { key: 'downstream', label: 'Downstream' }
];

/**
 * Admins and super admins get access to every team's queue from the staff sync
 * (server.integrator.helpdesk), so for them "My teams" is every ticket and is where they land.
 */
const SEES_ALL_TICKETS = [UserRole.ADMIN, UserRole.SUPER_ADMIN];

/** The hover text on the SLA cell: both targets. */
const slaTitle = (t: TicketUiModel): string =>
  [
    ['First response', t.sla.firstResponse],
    ['Resolution', t.sla.resolution]
  ]
    .map(([name, target]) => {
      const x = target as TicketUiModel['sla']['firstResponse'];
      return `${name}: ${titleCase(x.state)}${x.due ? `, due ${x.due.toLocaleString()}` : ''}`;
    })
    .join('\n');

const COLUMNS = [
  '#',
  'Priority',
  'SLA',
  'Title',
  'Category',
  'Team',
  'Owner',
  'State',
  'Created',
  'Updated'
];

export const TicketsQueueContainer: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  // The tab and "include closed" live in the URL so "back" from a ticket returns to the same queue.
  // Arriving without them (the menu, a handoff) picks up where this browser tab last left off.
  const [saved] = useState(loadQueueState);
  const search = location.search || saved.search || '';
  const params = new URLSearchParams(search);
  const seesAll = hasRole(SEES_ALL_TICKETS);
  // A Zammad Overview chosen as the queue (#697): ?view=overview&overview=<link>.
  const overview = params.get('view') === 'overview' ? params.get('overview') || '' : '';
  const view: TicketView = overview
    ? 'overview'
    : (VIEWS.find((v) => v.key === params.get('view'))?.key as TicketView) ??
      (seesAll ? 'team' : 'mine');
  const showClosed = params.get('closed') === '1';
  const queueUrl = (v: TicketView, closed: boolean, link?: string) =>
    v === 'overview' && link
      ? `?view=overview&overview=${encodeURIComponent(link)}`
      : `?view=${v}${closed ? '&closed=1' : ''}`;
  const [overviews, setOverviews] = useState<TicketOverviewModel[]>([]);

  const [tickets, setTickets] = useState<TicketUiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [filters, setFilters] = useState<QueueFilters>(saved.filters ?? EMPTY_FILTERS);
  const [sort, setSort] = useState<QueueSort>(saved.sort ?? 'updated');

  useEffect(() => {
    if (!location.search && search) history.replace({ search });
  }, [history, location.search, search]);

  useEffect(() => {
    saveQueueState({ search, filters, sort });
  }, [search, filters, sort]);
  // From the session cache when there is one, so the Downstream tab never blinks on a remount.
  const [me, setMe] = useState<HelpdeskMeModel>(
    () => cachedHelpdeskMe() ?? { isAdmin: false, canReassign: false, downstream: 0 }
  );
  const [now, setNow] = useState(Date.now());
  // Closed tickets are fetched only when asked for (CTO 2026-10-14): the toggle, or the
  // State filter set to Closed, which would otherwise always show an empty list.
  // An Overview's own conditions decide its states, so "include closed" does not apply to one.
  const includeClosed = !overview && (showClosed || filters.state === 'closed');
  const [showCreate, setShowCreate] = useState(false);
  const [showRouting, setShowRouting] = useState(false);
  /** Ticket id -> when it arrived on screen; highlighted until opened or NEW_HIGHLIGHT_MS. */
  const [arrived, setArrived] = useState<Map<number, number>>(new Map());
  const known = useRef<Set<number> | null>(null);

  // A refresh never blanks the list: only the first load of a queue shows the spinner, and a
  // failed background refresh keeps what is on screen.
  const load = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true);
      return getTickets(view, 1, includeClosed, overview).then((result) => {
        if (isErrorInfo(result)) {
          if (!silent) {
            setLoadError(describeLoadError(result));
            setTickets([]);
          }
        } else {
          setLoadError(undefined);
          const fresh = newArrivals(known.current, result);
          if (fresh.length) {
            setArrived((prev) => {
              const next = new Map(prev);
              fresh.forEach((id) => next.set(id, Date.now()));
              return next;
            });
          }
          known.current = new Set(result.map((t) => t.id));
          setTickets(result);
        }
        setLoading(false);
      });
    },
    [view, includeClosed, overview]
  );

  useEffect(() => {
    known.current = null; // a different queue: its first load marks nothing new
    setArrived(new Map());
    load();
  }, [load]);

  // Refresh itself while on screen, and as soon as the person comes back to the tab.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') load(true);
      setNow(Date.now());
      setArrived((prev) => {
        const cutoff = Date.now() - NEW_HIGHLIGHT_MS;
        const next = new Map(Array.from(prev).filter(([, at]) => at > cutoff));
        return next.size === prev.size ? prev : next;
      });
    };
    const timer = window.setInterval(tick, QUEUE_REFRESH_MS);
    const onVisible = () => document.visibilityState === 'visible' && load(true);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    getHelpdeskMe().then(setMe);
  }, []);

  // The Overviews (and their counts) on arrival and whenever the queue changes.
  useEffect(() => {
    getTicketOverviews().then(setOverviews);
  }, [view, overview]);

  // "New only" with nothing new left would be an empty list with no chip to undo it.
  useEffect(() => {
    if (filters.newOnly && arrived.size === 0) setFilters((f) => ({ ...f, newOnly: false }));
  }, [arrived, filters.newOnly]);

  const shown = useMemo(
    () => sortQueue(filterQueue(tickets, filters, new Set(arrived.keys())), sort),
    [tickets, filters, arrived, sort]
  );
  const options = useMemo(() => filterOptions(tickets), [tickets]);
  const visibleViews = VIEWS.filter(
    (v) => v.key !== 'downstream' || me.downstream > 0 || me.isAdmin
  ).map((v) => (seesAll && v.key === 'team' ? { ...v, label: 'All tickets' } : v));

  const detailsPath = (id: number) =>
    (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(
      ':ticketId',
      String(id)
    );

  return (
    <div className="min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500">
      <div className={`${CONTENT_CLASS} pt-[32px] pb-[24px]`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-[16px]">
          <div className="flex items-center gap-[10px] flex-wrap">
            <h1 className="m-0 text-[clamp(26px,2.6vw,40px)] leading-tight font-gilroy-bold text-neutral-500">
              Tickets
            </h1>
            {!loading && !loadError ? (
              <span
                className={`${HEADER_CHIP} bg-creams-100 border border-gray-200 text-neutral-500`}
              >
                {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
              </span>
            ) : null}
            {arrived.size ? (
              // Clickable: shows only the arrivals; click again for everything.
              <button
                type="button"
                role="status"
                aria-pressed={filters.newOnly}
                title={filters.newOnly ? 'Show all tickets' : 'Show only the new tickets'}
                className={`${HEADER_CHIP} cursor-pointer ${
                  filters.newOnly
                    ? 'bg-secondary-500 text-neutral-0'
                    : 'bg-secondary-50 text-secondary-700'
                }`}
                onClick={() => setFilters({ ...filters, newOnly: !filters.newOnly })}
              >
                {arrived.size} new{filters.newOnly ? ' · showing' : ''}
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-[6px] flex-wrap shrink-0">
            <button type="button" className={OUTLINE_BUTTON} onClick={() => setShowRouting(true)}>
              Routing
            </button>
            <button
              type="button"
              className={OUTLINE_BUTTON}
              onClick={() =>
                history.push(
                  (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).PERFORMANCE.FULL_PATH
                )
              }
            >
              Performance
            </button>
            <button type="button" className={PRIMARY_BUTTON} onClick={() => setShowCreate(true)}>
              Raise ticket
            </button>
          </div>
        </div>
      </div>

      <nav className="sticky top-0 z-40 bg-neutral-0 border-b border-gray-200">
        <div className={`${CONTENT_CLASS} flex items-center gap-[28px]`}>
          <div
            className="flex gap-[28px] overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
          >
            {visibleViews.map((v) => (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={v.key === view && !overview}
                className={`py-[16px] border-b-2 whitespace-nowrap text-[14px] font-gilroy-medium outline-none focus:outline-none focus-visible:text-neutral-500 cursor-pointer ${
                  v.key === view && !overview
                    ? 'border-neutral-500 text-neutral-500'
                    : 'border-transparent text-neutral-300 hover:text-neutral-500'
                }`}
                onClick={() => history.replace({ search: queueUrl(v.key, showClosed) })}
              >
                {v.label}
              </button>
            ))}
          </div>
          {overviews.length || overview ? (
            // The CX manager's Zammad Overviews, as queues (#697). Who sees which is set in Zammad.
            <label
              className={`flex shrink-0 items-center gap-[8px] py-[10px] text-[14px] font-gilroy-medium ${
                overview ? 'text-neutral-500' : 'text-neutral-300'
              }`}
            >
              Queue
              <select
                aria-label="Queue"
                className={`rounded-[10px] border px-[10px] py-[6px] text-[14px] text-neutral-500 ${
                  overview ? 'border-neutral-500' : 'border-gray-200'
                }`}
                value={overview}
                onChange={(e) =>
                  history.replace({
                    search: e.target.value
                      ? queueUrl('overview', false, e.target.value)
                      : queueUrl(seesAll ? 'team' : 'mine', showClosed)
                  })
                }
              >
                <option value="">Choose a queue…</option>
                {overview && !overviews.some((o) => o.link === overview) ? (
                  <option value={overview}>{overview}</option>
                ) : null}
                {overviews.map((o) => (
                  <option key={o.link} value={o.link}>
                    {o.name} ({o.count})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label
            className={`ml-auto flex shrink-0 items-center gap-[8px] text-[14px] text-neutral-500 ${
              overview ? 'opacity-50' : 'cursor-pointer'
            }`}
            title={overview ? 'A queue shows the states it is set up for in Zammad' : undefined}
          >
            <input
              type="checkbox"
              checked={showClosed && !overview}
              disabled={!!overview}
              onChange={(e) => history.replace({ search: queueUrl(view, e.target.checked) })}
            />
            Include closed
          </label>
        </div>
      </nav>

      <div className={`${CONTENT_CLASS} pb-[80px]`}>
        <section className="py-[24px]">
          <QueueFiltersBar
            filters={filters}
            sort={sort}
            options={options}
            onChange={setFilters}
            onSort={setSort}
          />
        </section>

        <section>
          {loading ? (
            <LoadingData />
          ) : loadError ? (
            <LoadError message={loadError} onRetry={() => load()} />
          ) : shown.length === 0 ? (
            <div className="rounded-[16px] bg-creams-100 px-[24px] py-[32px] text-center text-[15px] text-neutral-300">
              No tickets found
            </div>
          ) : (
            <div className="overflow-x-auto rounded-[12px] border border-gray-200">
              <table className="w-full min-w-[900px] border-collapse">
                <thead className="bg-creams-100">
                  <tr>
                    {COLUMNS.map((column: string) => (
                      <th key={column} className={TH}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {shown.map((ticket: TicketUiModel) => (
                    <tr
                      key={ticket.id}
                      className={`cursor-pointer transition-colors hover:bg-creams-100 ${
                        arrived.has(ticket.id) ? 'bg-secondary-50/40' : 'bg-neutral-0'
                      }`}
                      onClick={() => history.push(detailsPath(ticket.id))}
                    >
                      <td className={`${TD} font-gilroy-medium`}>
                        {ticket.number}
                        {arrived.has(ticket.id) ? (
                          <span className={`${CHIP} ml-2 bg-secondary-500 text-neutral-0`}>
                            New
                          </span>
                        ) : null}
                      </td>
                      <td className={TD}>
                        <span className="flex items-center gap-1.5">
                          <span className={`${CHIP} ${PRIORITY_STYLE[ticket.priority]}`}>
                            {ticket.priority}
                          </span>
                          {ticket.emergency ? (
                            <span className={`${CHIP} bg-secondary-500 text-neutral-0`}>
                              Emergency
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className={TD} title={slaTitle(ticket)}>
                        {ticket.sla.state === 'none' ? (
                          <span className="text-neutral-300">—</span>
                        ) : (
                          <span className="inline-flex flex-col items-center gap-[2px]">
                            <span className={`${CHIP} ${SLA_STYLE[ticket.sla.state]}`}>
                              {SLA_LABEL[ticket.sla.state]}
                            </span>
                            {ticket.sla.nextDue && !ticket.isClosed ? (
                              <span className="text-[12px] text-neutral-300">
                                {dueIn(ticket.sla.nextDue, now)}
                              </span>
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td className={`${TD} max-w-[240px] truncate font-gilroy-medium`}>
                        {ticket.title}
                      </td>
                      <td className={`${TD} text-neutral-300`} title={ticket.categoryLabel}>
                        {ticket.categoryCode ?? (
                          // A guest email arrives without one; someone in the team sets it.
                          <span className={`${CHIP} bg-creams-600 text-creams-900`}>
                            Needs category
                          </span>
                        )}
                      </td>
                      <td className={TD}>{ticket.team}</td>
                      <td className={TD}>
                        {ticket.ownerName ?? (
                          <span className={`${CHIP} bg-creams-500 text-creams-900`}>
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className={TD}>
                        <span className={`${CHIP} ${stateStyle(ticket)}`}>
                          {titleCase(ticket.state)}
                        </span>
                      </td>
                      <td
                        className={`${TD} text-neutral-300`}
                        title={ticket.createdAt.toLocaleString()}
                      >
                        {moment(ticket.createdAt).format('D MMM, HH:mm')}
                      </td>
                      <td
                        className={`${TD} text-neutral-300`}
                        title={ticket.updatedAt.toLocaleString()}
                      >
                        {moment(ticket.updatedAt).format('D MMM, HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <RoutingModal show={showRouting} onClose={() => setShowRouting(false)} />
      <RaiseTicketModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => load(true)}
      />
    </div>
  );
};
